import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiError,
  type AssignmentRole,
  type ClientAssignment,
  type Office,
  type OfficeAnnualPanel,
  type OfficeClient,
  type OfficeDashboard,
  type OfficeMember,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, nip as formatNip } from '../format';
import * as Icon from '../components/Icons';
import { TopControls, useDensity } from '../components/Shell';
import { PaymentMethods } from '../components/PaymentMethods';
import { OfficeBillingPanel } from '../components/OfficeBilling';
import { OfficeJpk } from '../components/OfficeJpk';

const currentMonth = () => new Date().toISOString().slice(0, 7);

/**
 * Ekran 24: panel biura rachunkowego. Stoi poza kontekstem firmy — obsługuje ich wiele naraz,
 * więc nie ma bocznej nawigacji jednej z nich.
 */
export function OfficePanel({
  t,
  locale,
  account,
  onBack,
  onLocale,
  onSignOut,
}: {
  t: Dictionary;
  locale: Locale;
  account: { fullName: string };
  onBack: () => void;
  onLocale: (next: Locale) => void;
  onSignOut: () => void;
}) {
  const [offices, setOffices] = useState<Office[] | null>(null);
  const [officeId, setOfficeId] = useState<string | null>(null);
  const [month, setMonth] = useState(currentMonth);
  const [onlyMine, setOnlyMine] = useState(false);
  const [dashboard, setDashboard] = useState<OfficeDashboard | null>(null);
  /**
   * Rok obrotowy klientów osobno od panelu miesięcznego.
   *
   * Terminy roczne nie mają nic wspólnego z miesiącem, więc ta siatka ma własny rok i własne
   * zapytanie — inaczej zmiana miesiąca przeładowywałaby dane, które od miesiąca nie zależą.
   */
  const [annualYear, setAnnualYear] = useState(() => new Date().getFullYear() - 1);
  const [annual, setAnnual] = useState<OfficeAnnualPanel | null>(null);
  const [members, setMembers] = useState<OfficeMember[]>([]);
  const [clients, setClients] = useState<OfficeClient[]>([]);
  const [form, setForm] = useState<{ nip: string; name: string } | null>(null);
  const [assignments, setAssignments] = useState<ClientAssignment[]>([]);
  /** Kogo i w jakiej roli przydzielamy — osobno dla każdej umowy, bo formularzy jest tyle co klientów. */
  const [assigning, setAssigning] = useState<Record<string, { userId: string; role: AssignmentRole }>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [density, setDensity] = useDensity();

  const loadOffices = useCallback(async () => {
    try {
      const mine = await api.offices();
      setOffices(mine);
      setOfficeId((current) => current ?? mine[0]?.id ?? null);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, []);

  useEffect(() => {
    void loadOffices();
  }, [loadOffices]);

  const loadOffice = useCallback(async () => {
    if (!officeId) return;
    try {
      const [board, team, list, assigned] = await Promise.all([
        api.officeDashboard(officeId, month, onlyMine),
        api.officeMembers(officeId),
        api.officeClients(officeId),
        api.officeAssignments(officeId),
      ]);
      setDashboard(board);
      setMembers(team.filter((member) => !member.validTo));
      setClients(list.filter((client) => !client.validTo));
      setAssignments(assigned.filter((assignment) => !assignment.validTo));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [officeId, month, onlyMine]);

  useEffect(() => {
    void loadOffice();
  }, [loadOffice]);

  const loadAnnual = useCallback(async () => {
    if (!officeId) return;
    try {
      setAnnual(await api.officeAnnual(officeId, annualYear, onlyMine));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [officeId, annualYear, onlyMine]);

  useEffect(() => {
    void loadAnnual();
  }, [loadAnnual]);

  const office = offices?.find((item) => item.id === officeId);
  const manages = office?.myRole === 'OFFICE_OWNER' || office?.myRole === 'OFFICE_ADMIN';

  /** Wspólna obsługa czynności: zajętość, błąd, odświeżenie danych biura po udanej zmianie. */
  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await loadOffice();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  async function createOffice(event: React.FormEvent) {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createOffice(form.nip, form.name);
      setForm(null);
      setOfficeId(created.id);
      await loadOffices();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app solo">
      <div className="main">
        <div className="top">
          <div className="crumb">
            <b>{office ? office.name : t.office.title}</b>
          </div>
          <TopControls t={t} locale={locale} density={density} onDensity={setDensity} onLocale={onLocale}>
            <button type="button" className="btn sec sm" onClick={onBack}>
              {t.office.companiesTab}
            </button>
            <span className="row" style={{ gap: 8 }}>
              <span className="sm">{account.fullName}</span>
              <button type="button" className="btn ghost sm" onClick={onSignOut}>
                {t.signOut}
              </button>
            </span>
          </TopControls>
        </div>

        <div className="body">
          <div className="hd">
            <div>
              <h3>{t.office.title}</h3>
              <p>{t.office.subtitle}</p>
            </div>
            <div className="sp">
              {offices && offices.length > 1 && (
                <select
                  className="inp w"
                  aria-label={t.office.title}
                  value={officeId ?? ''}
                  onChange={(event) => setOfficeId(event.target.value)}
                >
                  {offices.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {error && (
            <div className="alert a-bad">
              <Icon.Error />
              <div>
                <b>{error}</b>
              </div>
            </div>
          )}

          {offices === null && <p className="muted">{t.loading}</p>}

          {/*
           * Pusty stan ma dwa wyjścia, nie jedno: częstszy przypadek to księgowa czekająca
           * na zaproszenie od właściciela biura, a nie osoba zakładająca własne.
           */}
          {offices?.length === 0 && !form && (
            <div className="panel">
              <div className="empty">
                <Icon.Office />
                <b>{t.office.none}</b>
                <p>{t.office.noneHint}</p>
                <div className="row" style={{ justifyContent: 'center' }}>
                  <button type="button" className="btn" onClick={() => setForm({ nip: '', name: '' })}>
                    {t.office.create}
                  </button>
                </div>
              </div>
            </div>
          )}

          {form && (
            <form className="panel" onSubmit={createOffice}>
              <header>
                <h4>{t.office.create}</h4>
              </header>
              <div className="fields">
                <div className="field">
                  <label htmlFor="officeName">{t.office.name}</label>
                  <input
                    id="officeName"
                    className="inp"
                    value={form.name}
                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="officeNip">{t.office.nip}</label>
                  <input
                    id="officeNip"
                    className="inp mono"
                    value={form.nip}
                    onChange={(event) => setForm({ ...form, nip: event.target.value })}
                    inputMode="numeric"
                    required
                  />
                </div>
              </div>
              <div className="bar2">
                <button type="submit" className="btn" disabled={busy}>
                  {busy ? t.companies.saving : t.office.create}
                </button>
                <button type="button" className="btn ghost" onClick={() => setForm(null)} disabled={busy}>
                  {t.companies.cancel}
                </button>
              </div>
            </form>
          )}

          {office && (
            <div className="panel">
              <header>
                <h4>{t.office.clients}</h4>
                <div className="sp">
                  <input
                    className="inp w sm"
                    type="month"
                    aria-label={t.office.month}
                    value={month}
                    onChange={(event) => setMonth(event.target.value)}
                  />
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={onlyMine}
                      onChange={(event) => setOnlyMine(event.target.checked)}
                    />
                    {t.office.onlyMine}
                  </label>
                </div>
              </header>

              {dashboard?.clients.length === 0 && (
                <div className="empty">
                  <b>{t.office.noClients}</b>
                </div>
              )}

              {dashboard && dashboard.clients.length > 0 && (
                <div className="tbl">
                  <table>
                    <thead>
                      <tr>
                        <th>{t.office.client}</th>
                        <th>{t.office.assigned}</th>
                        <th className="n">{t.office.invoices}</th>
                        <th className="n">{t.office.costs}</th>
                        <th className="n">{t.office.revenue}</th>
                        <th>{t.office.state}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.clients.map((client) => (
                        <tr key={client.companyId}>
                          <td>
                            {client.companyName}
                            <span className="muted sm mono"> {formatNip(client.companyNip)}</span>
                          </td>
                          <td className="muted sm">{client.assignedTo.join(', ') || '—'}</td>
                          <td className="n">
                            {client.invoicesIssued}
                            {client.invoiceDrafts > 0 && (
                              <span className="muted"> +{client.invoiceDrafts}</span>
                            )}
                          </td>
                          <td className="n">
                            {client.costsBooked}
                            {client.costDrafts > 0 && <span className="muted"> +{client.costDrafts}</span>}
                          </td>
                          <td className="n">{amount(client.revenue, locale)}</td>
                          <td>
                            {/* Lista zadań wprost z danych — nic tu nie odhacza się ręcznie */}
                            {client.readyToSettle ? (
                              <span className="chip st-ok">
                                <i />
                                {t.office.ready}
                              </span>
                            ) : (
                              <span className="row" style={{ gap: 4 }}>
                                {client.warnings.map((warning) => (
                                  <span className="chip st-warn" key={warning}>
                                    <i />
                                    {warning}
                                  </span>
                                ))}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {office && (
            <div className="panel">
              <header>
                <h4>{t.annual.office.title}</h4>
                <div className="sp">
                  <input
                    className="inp w sm"
                    type="number"
                    aria-label={t.annual.office.year}
                    value={annualYear}
                    onChange={(event) => setAnnualYear(Number(event.target.value))}
                  />
                </div>
              </header>

              {annual?.clients.length === 0 && (
                <div className="empty">
                  <b>{t.annual.office.none}</b>
                </div>
              )}

              {annual && annual.clients.length > 0 && (
                <div className="tbl">
                  <table>
                    <thead>
                      <tr>
                        <th>{t.annual.office.client}</th>
                        <th>{t.annual.office.status}</th>
                        <th>{t.annual.office.prepared}</th>
                        <th>{t.annual.office.approved}</th>
                        <th>{t.annual.office.filed}</th>
                        <th>{t.annual.office.mention}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {annual.clients.map((client) => (
                        <tr key={client.companyId}>
                          <td>
                            {client.companyName}
                            <span className="muted sm mono"> {client.krs ?? formatNip(client.companyNip)}</span>
                          </td>
                          <td>
                            {client.status === null ? (
                              <span className="chip st-warn">
                                <i />
                                {t.annual.office.noReport}
                              </span>
                            ) : (
                              <span className={client.status === 'confirmed' ? 'chip st-ok' : 'chip st-info'}>
                                <i />
                                {t.annual.statuses[client.status]}
                              </span>
                            )}
                          </td>
                          <td className="doc">{client.preparedOn ?? '—'}</td>
                          <td className="doc">{client.approvedOn ?? '—'}</td>
                          <td className="doc">{client.filedOn ?? '—'}</td>
                          {/* Wzmianka w rejestrze to jedyny dowód złożenia niezależny od klienta */}
                          <td>{client.mentionInRegister ? '✓' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {office && manages && (
            <div className="grid two">
              <div className="panel">
                <header>
                  <h4>{t.office.members}</h4>
                </header>
                <div className="tbl">
                  <table>
                    <tbody>
                      {members.map((member) => (
                        <tr key={member.membershipId}>
                          <td>{member.fullName}</td>
                          <td className="muted sm">{member.email}</td>
                          <td>{t.office.roles[member.role]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="panel">
                <header>
                  <h4>{t.office.clients}</h4>
                </header>
                <div className="tbl">
                  <table>
                    <tbody>
                      {clients.map((client) => (
                        <tr key={client.id}>
                          <td>{client.companyName}</td>
                          <td className="mono sm">{formatNip(client.companyNip)}</td>
                          <td>
                            {/* Propozycja bez zgody firmy nie daje ani dostępu, ani stawki biura */}
                            <span className={client.status === 'accepted' ? 'chip st-ok' : 'chip st-warn'}>
                              <i />
                              {client.status === 'accepted' ? t.office.served : t.office.awaitingConsent}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/*
            * Przydziały: dopiero one otwierają dostęp do ksiąg klienta. Sama umowa mówi, że biuro
            * obsługuje firmę — nie mówi, kto z biura ma do niej wejść. Klientów bywa pięćdziesięciu,
            * więc przydziałów jest więcej niż jeden na firmę: prowadząca, zastępstwo i podgląd.
            */}
          {office && manages && (
            <div className="panel">
              <header>
                <h4>{t.office.assignments}</h4>
              </header>
              <p className="note">{t.office.assignmentsHint}</p>

              {clients.filter((client) => client.status === 'accepted').length === 0 && (
                <div className="empty" style={{ padding: 'var(--space-6)' }}>
                  <b>{t.office.noServedClients}</b>
                </div>
              )}

              {clients
                .filter((client) => client.status === 'accepted')
                .map((client) => {
                  const mine = assignments.filter((assignment) => assignment.agreementId === client.id);
                  const draft = assigning[client.id] ?? { userId: '', role: 'LEAD' as AssignmentRole };
                  const free = members.filter(
                    (member) => !mine.some((assignment) => assignment.userId === member.userId),
                  );
                  return (
                    <div className="fields" key={client.id} style={{ marginBottom: 'var(--space-4)' }}>
                      <div>
                        <b>{client.companyName}</b>
                        <span className="muted sm mono"> {formatNip(client.companyNip)}</span>
                      </div>
                      <div className="row">
                        {mine.length === 0 && <span className="muted sm">{t.office.nobodyAssigned}</span>}
                        {mine.map((assignment) => (
                          <span className="chip st-info" key={assignment.id}>
                            <i />
                            {assignment.userName} · {t.office.assignmentRoles[assignment.role]}
                            <button
                              type="button"
                              className="btn plain sm"
                              disabled={busy}
                              onClick={() =>
                                void run(() => api.endAssignment(office.id, assignment.id))
                              }
                              aria-label={`${t.office.endAssignment} — ${assignment.userName}`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      {free.length > 0 && (
                        <div className="row">
                          <select
                            className="inp sm"
                            aria-label={`${t.office.assignPerson} — ${client.companyName}`}
                            value={draft.userId}
                            onChange={(event) =>
                              setAssigning({ ...assigning, [client.id]: { ...draft, userId: event.target.value } })
                            }
                          >
                            <option value="">{t.office.assignPerson}</option>
                            {free.map((member) => (
                              <option key={member.userId} value={member.userId}>
                                {member.fullName}
                              </option>
                            ))}
                          </select>
                          <select
                            className="inp sm"
                            aria-label={`${t.office.assignmentRole} — ${client.companyName}`}
                            value={draft.role}
                            onChange={(event) =>
                              setAssigning({
                                ...assigning,
                                [client.id]: { ...draft, role: event.target.value as AssignmentRole },
                              })
                            }
                          >
                            {(['LEAD', 'BACKUP', 'VIEWER'] as const).map((role) => (
                              <option key={role} value={role}>
                                {t.office.assignmentRoles[role]}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn sec sm"
                            disabled={busy || !draft.userId}
                            onClick={() =>
                              void run(async () => {
                                await api.assignAccountant(office.id, client.id, draft.userId, draft.role);
                                setAssigning({ ...assigning, [client.id]: { userId: '', role: draft.role } });
                              })
                            }
                          >
                            {t.office.assign}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}

          {office && (
            <>
              <OfficeJpk officeId={office.id} month={month} clients={clients} t={t} />
              <OfficeBillingPanel officeId={office.id} t={t} locale={locale} />
              <PaymentMethods scope={{ kind: 'offices', id: office.id }} canManage={manages} t={t} locale={locale} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
